import React, { useEffect, useState, useRef } from 'react';
import { Badge } from '@mui/material';
import socket from '../socket';
import apiCIP from '../services/apiCIP';

interface Chat {
    _id: string;
    lastMessage: {
        status: string;
        sender: {
            _id: string;
        };
        receiver?: {
            _id: string;
        };
    };
}

interface MessageNotificationsProps {
    maxCount?: number;
    color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

const MessageNotifications: React.FC<MessageNotificationsProps> = ({
    maxCount = 99,
    color = 'secondary',
}) => {
    const cipUserId = process.env.REACT_APP_CIP_USER_ID;
    const cipInstituicaoId = process.env.REACT_APP_CIP_INSTITUICAO_ID;

    const [chats, setChats] = useState<Chat[]>([]);
    const [error, setError] = useState<string | null>(null);
    const joinedChatsRef = useRef<Set<string>>(new Set());
   
    const fetchChats = async () => {
        try {
            const response = await apiCIP.get(`/meus/chats/${cipUserId}?instituicaoId=${cipInstituicaoId}&Perpage=100`);
            setChats(response.data.chats);
            setError(null);

            // Join nos chats apenas se ainda não estiver no room
            response.data.chats.forEach((chat: Chat) => {
                if (!joinedChatsRef.current.has(chat._id)) {
                    socket.emit('join_chat', chat._id);
                    joinedChatsRef.current.add(chat._id);
                }
            });
        } catch (err: any) {
            setError(err.response?.data?.message || 'Erro ao carregar os chats');
        } 
    };

    // Carregar chats apenas uma vez ao montar o componente
    useEffect(() => {
        if (cipUserId) {
            fetchChats();
        }

        // Cleanup ao desmontar
        return () => {
            joinedChatsRef.current.forEach(chatId => {
                socket.emit('leave_chat', chatId);
            });
            joinedChatsRef.current.clear();
        };
    }, [cipUserId, cipInstituicaoId]); // Dependências corretas

    // Socket listener para novas mensagens
    useEffect(() => {
        const handleNewMessage = (newMessage: any) => {
            if (!newMessage?.chat) return;

            setChats(prevChats => {
                const chatId = typeof newMessage.chat === 'string' ? newMessage.chat : newMessage.chat._id;

                const updatedChats = prevChats.map(chat => {
                    if (chat._id === chatId) {
                        return {
                            ...chat,
                            lastMessage: newMessage.chat.lastMessage || newMessage,
                        };
                    }
                    return chat;
                });

                return updatedChats;
            });
        };

        socket.on('message', handleNewMessage);

        return () => {
            socket.off('message', handleNewMessage);
        };
    }, []);

    // Calcular mensagens não lidas
    const unreadCount = chats.filter(chat => {
        return (
            !chat.lastMessage?.receiver && chat.lastMessage?.status !== 'Lida' 
        );
    }).length;

    return (
        <Badge
            color={color}
            badgeContent={unreadCount}
            max={maxCount}
            invisible={unreadCount === 0}
            sx={{
                '& .MuiBadge-badge': {
                    right: -3,
                    top: 5,
                    padding: '0 4px',
                    backgroundColor: 'var(--color-primary-500, #f97316)',
                    color: 'white',
                    fontWeight: 'bold',
                },
            }}
        >
            <img src={'/icones/OFAfaleconosco.svg'} width={35} alt="icon" />
        </Badge>
    );
};

export default MessageNotifications;